/**
 * ConnectedAccount — the ONE account-identity shape (multi-account design §5).
 *
 * Composio is the sole source of truth for account identity: there is no mirror
 * table, and the label IS the Composio alias. This module holds the canonical
 * camelCase record, its snake_case wire projection, and the two pure judgements
 * every surface needs, so server / web / sandbox never re-derive them.
 *
 * The status union is WIDER than the legacy per-TOOLKIT `ConnectionInfo['status']`
 * on purpose: 0.17.0's ConnectedAccountStatusEnum added REVOKED and INACTIVE, and a
 * per-account row must be able to say which one it is. The toolkit-row union is
 * deliberately left alone (widening it would ripple into every existing consumer).
 */

/** Our vocabulary for a Composio connected-account status. */
export type ConnectedAccountStatus =
  | 'active'
  | 'pending'
  | 'initializing'
  | 'expired'
  | 'revoked'
  | 'inactive'
  | 'failed';

/** One connected account, exactly as Composio knows it. `alias` is the label. */
export interface ConnectedAccount {
  /** Composio connected-account id (`ca_*`) — the only durable key we may store. */
  id: string;
  /** Lowercased toolkit slug. */
  toolkit: string;
  status: ConnectedAccountStatus;
  /** The auth config this account was minted under; null when Composio omits it. */
  authConfigId: string | null;
  /** Composio alias = our label. null until the identity probe writes one. */
  alias: string | null;
  /** ISO-8601. */
  createdAt: string;
}

/** The snake_case projection served on `/api/integrations/*` (wire house style). */
export interface ConnectedAccountWire {
  id: string;
  toolkit: string;
  status: ConnectedAccountStatus;
  auth_config_id: string | null;
  alias: string | null;
  created_at: string;
  /**
   * The account's own profile picture, when we have one.
   *
   * Composio does not store this — it comes from a provider profile read and is
   * cached in `connected_account_avatars`, so it is joined on at the route
   * rather than carried on ConnectedAccount. Absent or null means the keyring
   * falls back to the app's mark, which is always available.
   *
   * Deliberately NOT on AccountSummary: the runner selects accounts by label,
   * never by picture, and the roster env is a spawn payload to keep small.
   */
  avatar_url?: string | null;
}

/**
 * Composio's raw status string → our vocabulary. Unknown values read 'pending'
 * (the same fail-soft the legacy toolkit mapper uses): a status Composio adds
 * tomorrow must never crash a listing.
 */
export function connectedAccountStatusFrom(raw: string | null | undefined): ConnectedAccountStatus {
  switch (String(raw ?? '').toUpperCase()) {
    case 'ACTIVE':
      return 'active';
    case 'EXPIRED':
      return 'expired';
    case 'REVOKED':
      return 'revoked';
    case 'INACTIVE':
      return 'inactive';
    case 'FAILED':
      return 'failed';
    case 'INITIALIZING':
      return 'initializing';
    default:
      return 'pending';
  }
}

/**
 * The account cannot serve a tool call and only a NEW consent can fix it.
 * REVOKED and INACTIVE join EXPIRED and FAILED here — the design's
 * "REVOKED is treated like EXPIRED" ruling, in one place instead of at each site.
 */
export function accountNeedsReconnect(status: ConnectedAccountStatus): boolean {
  return status === 'expired' || status === 'revoked' || status === 'inactive' || status === 'failed';
}

/** Canonical → wire. The ONLY place the two shapes are allowed to meet. */
export function toConnectedAccountWire(account: ConnectedAccount): ConnectedAccountWire {
  return {
    id: account.id,
    toolkit: account.toolkit,
    status: account.status,
    auth_config_id: account.authConfigId,
    alias: account.alias,
    created_at: account.createdAt,
  };
}

/**
 * AccountSummary — the runner-facing slice of a ConnectedAccount (design §7).
 * This is what rides the COMPOSIO_CONNECTED_ACCOUNTS spawn env and the sandbox
 * roster cache; keeping it minimal keeps the env payload small and the runner
 * blind to fields it must not depend on (authConfigId, createdAt).
 */
export interface AccountSummary {
  id: string;
  alias: string | null;
  status: ConnectedAccountStatus;
}

/** Env/wire projection of the whole roster: lowercase toolkit slug → accounts. */
export type ConnectedAccountsRoster = Record<string, AccountSummary[]>;

/** Canonical record → runner summary. */
export function accountSummaryFrom(account: ConnectedAccount): AccountSummary {
  return { id: account.id, alias: account.alias, status: account.status };
}

/** Wire record (snake_case /api/integrations rows) → runner summary. */
export function accountSummaryFromWire(wire: ConnectedAccountWire): AccountSummary {
  return { id: wire.id, alias: wire.alias, status: wire.status };
}

/**
 * Serialize a roster for the spawn env. Keys sorted and lowercased so the same
 * account set always produces the same bytes (the entry is volatile-tagged and
 * outside every fingerprint, but deterministic bytes keep tests and diffs sane).
 */
export function serializeAccountsRoster(roster: ConnectedAccountsRoster): string {
  const sorted: ConnectedAccountsRoster = {};
  for (const slug of Object.keys(roster).sort()) {
    sorted[slug.toLowerCase()] = roster[slug];
  }
  return JSON.stringify(sorted);
}

/**
 * Parse the roster env/JSON. TOTAL: any malformed payload returns undefined —
 * the sandbox treats undefined as "legacy mode" (fail-safe: selection gating
 * simply stays off and the router's own 400 backstops multi-account calls).
 * Unknown statuses fail-soft to 'pending' via connectedAccountStatusFrom.
 */
export function parseAccountsRosterJson(
  json: string | undefined,
): Map<string, AccountSummary[]> | undefined {
  if (!json) return undefined;
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
    const roster = new Map<string, AccountSummary[]>();
    for (const [slug, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!Array.isArray(value)) return undefined;
      const accounts: AccountSummary[] = [];
      for (const entry of value) {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return undefined;
        const { id, alias, status } = entry as Record<string, unknown>;
        if (typeof id !== 'string' || id.length === 0) return undefined;
        if (alias !== null && typeof alias !== 'string') return undefined;
        if (typeof status !== 'string') return undefined;
        accounts.push({ id, alias: alias as string | null, status: connectedAccountStatusFrom(status) });
      }
      roster.set(slug.toLowerCase(), accounts);
    }
    return roster;
  } catch {
    return undefined;
  }
}
